// src/components/features/profile/AffiliatedRewardsSection.js
import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
} from "@mui/material";

/**
 * Props:
 * - programs: [{ key, label }]
 * - rewards: [{ programKey, programName, memberId }]
 * - onUpsert(programKey, programName, memberId)
 * - onRemove(programKey)
 * - textFieldStyles(theme)
 */
export default function AffiliatedRewardsSection({
  programs = [],
  rewards = [],
  onUpsert,
  onRemove,
  textFieldStyles,
}) {
  const [isAdding, setIsAdding] = useState(false);
  const [selectedProgramKey, setSelectedProgramKey] = useState("");
  const [draftMemberId, setDraftMemberId] = useState("");

  const existingMap = useMemo(() => {
    const map = {};
    (rewards || []).forEach((r) => {
      map[r.programKey] = r;
    });
    return map;
  }, [rewards]);

  const availablePrograms = useMemo(
    () => programs.filter((program) => !existingMap[program.key]),
    [programs, existingMap]
  );

  useEffect(() => {
    if (!isAdding) return;
    if (!selectedProgramKey && availablePrograms.length > 0) {
      setSelectedProgramKey(availablePrograms[0].key);
    }
    if (availablePrograms.length === 0) {
      setSelectedProgramKey("");
    }
  }, [isAdding, selectedProgramKey, availablePrograms]);

  const selectedLabel =
    availablePrograms.find((p) => p.key === selectedProgramKey)?.label || "";

  const openAddForm = () => {
    setIsAdding(true);
    setDraftMemberId("");
    setSelectedProgramKey((prev) => prev || availablePrograms[0]?.key || "");
  };

  const closeAddForm = () => {
    setIsAdding(false);
    setDraftMemberId("");
    setSelectedProgramKey("");
  };

  const handleAddReward = () => {
    if (!selectedProgramKey) return;
    const program = availablePrograms.find((item) => item.key === selectedProgramKey);
    if (!program) return;
    onUpsert(program.key, program.label, draftMemberId);
    closeAddForm();
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        Connect the programs you already use so your rewards details stay attached to your profile.
      </Typography>

      <Stack spacing={2.5}>
        {rewards.length > 0 ? (
          rewards.map((reward) => (
            <Box
              key={reward.programKey}
              sx={{
                py: 1.75,
                borderBottom: "1px solid",
                borderBottomColor: "divider",
              }}
            >
              <Stack spacing={1.25}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                >
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {reward.programName || reward.programKey}
                  </Typography>
                  <Chip label="Connected" size="small" color="success" variant="outlined" />
                </Stack>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  alignItems={{ xs: "stretch", sm: "flex-end" }}
                >
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      label="Member ID / Number"
                      name={`affiliate-${reward.programKey}`}
                      value={reward.memberId || ""}
                      onChange={(e) =>
                        onUpsert(reward.programKey, reward.programName, e.target.value)
                      }
                      variant="outlined"
                      inputProps={{
                        "aria-label": `${reward.programName || reward.programKey} ID or Number`,
                        autoComplete: "off",
                      }}
                      sx={(theme) => ({
                        ...(textFieldStyles ? textFieldStyles(theme) : {}),
                        "& .MuiOutlinedInput-root": {
                          ...(textFieldStyles ? textFieldStyles(theme)["& .MuiOutlinedInput-root"] : {}),
                          minHeight: 56,
                        },
                      })}
                    />
                  </Box>

                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => onRemove(reward.programKey)}
                    sx={{ minWidth: { sm: 124 }, px: 2.5, minHeight: 56 }}
                  >
                    Remove
                  </Button>
                </Stack>
              </Stack>
            </Box>
          ))
        ) : (
          <Box
            sx={{
              py: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              No affiliate programs connected yet
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Add one below to start tracking rewards on your profile.
            </Typography>
          </Box>
        )}

        {isAdding && availablePrograms.length > 0 && (
          <Box
            sx={{
              pt: 1.5,
              borderTop: "1px solid",
              borderTopColor: "divider",
            }}
          >
            <Stack spacing={2} sx={{ width: "100%", maxWidth: 560, mx: "auto" }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Add affiliate program
              </Typography>

              <FormControl fullWidth>
                <InputLabel id="affiliate-program-label">Program</InputLabel>
                <Select
                  labelId="affiliate-program-label"
                  label="Program"
                  value={selectedProgramKey}
                  onChange={(e) => setSelectedProgramKey(e.target.value)}
                  sx={{
                    minHeight: 56,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.25,
                      minHeight: 56,
                    },
                    "& .MuiOutlinedInput-notchedOutline": { borderRadius: 1.25 },
                  }}
                >
                  {availablePrograms.map((p) => (
                    <MenuItem key={p.key} value={p.key}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Member ID / Number"
                name="affiliateMemberId"
                value={draftMemberId}
                onChange={(e) => setDraftMemberId(e.target.value)}
                variant="outlined"
                inputProps={{
                  "aria-label": selectedLabel
                    ? `${selectedLabel} ID or Number`
                    : "Program ID or Number",
                  autoComplete: "off",
                }}
                sx={(theme) => ({
                  ...(textFieldStyles ? textFieldStyles(theme) : {}),
                  "& .MuiOutlinedInput-root": {
                    ...(textFieldStyles ? textFieldStyles(theme)["& .MuiOutlinedInput-root"] : {}),
                    minHeight: 56,
                  },
                })}
              />

              <Stack direction="row" spacing={1.25} justifyContent="space-between">
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={closeAddForm}
                  sx={{ minWidth: 120 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAddReward}
                  disabled={!selectedProgramKey}
                  sx={{ minWidth: 160 }}
                >
                  Add program
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}

        {!isAdding && availablePrograms.length > 0 && (
          <Button variant="outlined" onClick={openAddForm} sx={{ alignSelf: "flex-start", px: 2.5 }}>
            Add affiliate program
          </Button>
        )}

        {availablePrograms.length === 0 && rewards.length > 0 && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            All available affiliate programs are already connected.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
